/**
 * Pure helpers for the TP/SL of an open position (Position Details panel,
 * editor and chart lines). No runtime imports, so Jest can run them.
 */

export type ProtectionKind = "takeProfit" | "stopLoss";
export type PositionSide = "LONG" | "SHORT";

export const TAKE_PROFIT_PILLS = [5, 10, 25, 50, 100];
export const STOP_LOSS_PILLS = [2, 5, 10, 20];
export const DEFAULT_TAKE_PROFIT_PERCENT = 10;
export const DEFAULT_STOP_LOSS_PERCENT = 5;

export const toPositivePrice = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const decimalsOf = (value: string | number): number | null => {
  const text = String(value).trim();
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const fraction = text.split(".")[1]?.replace(/0+$/, "") ?? "";
  return fraction.length;
};

/** Decimals needed to show `price` with 5 significant digits. */
const significantDecimals = (price: number): number => {
  const integerDigits = Math.floor(Math.log10(price)) + 1;
  return Math.max(0, 5 - integerDigits);
};

/**
 * Decimals a TP/SL price is rounded to: the entry price's (the exchange's
 * tick, as far as we can tell), or 5 significant digits, whichever is finer.
 */
export function protectionPriceDecimals(
  price: number,
  entryPrice?: string | number | null
): number {
  const fromEntry =
    entryPrice === null || entryPrice === undefined ? null : decimalsOf(entryPrice);
  return Math.min(10, Math.max(fromEntry ?? 0, significantDecimals(price)));
}

/** A price rounded for the input box, e.g. "71500" or "0.012345". */
export function roundProtectionPrice(
  price: number,
  entryPrice?: string | number | null
): string {
  const decimals = protectionPriceDecimals(price, entryPrice);
  return String(Number(price.toFixed(decimals)));
}

/**
 * A TP/SL price for display: `$71,500`, `$64,987.5`, `$0.012345`. Uses the
 * entry price's decimals and keeps any extra decimals the exchange reports.
 */
export function formatProtectionPrice(
  price: number,
  entryPrice?: string | number | null
): string {
  const own = decimalsOf(String(Number(price.toPrecision(12)))) ?? 0;
  const fromEntry =
    entryPrice === null || entryPrice === undefined ? null : decimalsOf(entryPrice);
  const decimals = Math.min(
    10,
    Math.max(own, fromEntry ?? significantDecimals(price))
  );
  return `$${price.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/** Price move from entry in %, positive when it is in the position's favour. */
export function favourableMovePercent(
  price: number,
  entryPrice: number,
  side: PositionSide
): number {
  if (!(entryPrice > 0)) return 0;
  const move = ((price - entryPrice) / entryPrice) * 100;
  return side === "LONG" ? move : -move;
}

/** Signed price move from entry as the chart shows it (+ above, − below). */
export function priceMovePercent(price: number, entryPrice: number): number {
  if (!(entryPrice > 0)) return 0;
  return ((price - entryPrice) / entryPrice) * 100;
}

export interface ProtectionOutcome {
  /** Price move from entry (+ above entry, − below). */
  movePercent: number;
  /** Return on margin, sign-correct for shorts. */
  roiPercent: number;
  /** Estimated PnL in USDT: margin × ROI. */
  pnl: number;
}

export function protectionOutcome(args: {
  price: number;
  entryPrice: number;
  side: PositionSide;
  leverage: number;
  usdAmount: number;
}): ProtectionOutcome {
  const { price, entryPrice, side, leverage, usdAmount } = args;
  const roiPercent = favourableMovePercent(price, entryPrice, side) * (leverage || 1);
  return {
    movePercent: priceMovePercent(price, entryPrice),
    roiPercent,
    pnl: (usdAmount || 0) * (roiPercent / 100),
  };
}

const trimNumber = (value: number, digits: number): string =>
  String(Number(value.toFixed(digits)));

/** "+12.4%" / "−5%" (one decimal, no trailing zero). */
export function formatSignedPercent(value: number, digits = 1): string {
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return "0%";
  const sign = rounded > 0 ? "+" : "−";
  return `${sign}${trimNumber(Math.abs(rounded), digits)}%`;
}

/** "+$20.00" / "−$7.50". */
export function formatSignedUsd(value: number): string {
  const rounded = Number(value.toFixed(2));
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
  return `${sign}$${Math.abs(rounded).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** "+12.4% from entry · ROI +124%" */
export function protectionSummary(
  price: number,
  args: { entryPrice: number; side: PositionSide; leverage: number }
): string {
  const { movePercent, roiPercent } = protectionOutcome({
    price,
    entryPrice: args.entryPrice,
    side: args.side,
    leverage: args.leverage,
    usdAmount: 0,
  });
  return `${formatSignedPercent(movePercent)} from entry · ROI ${formatSignedPercent(roiPercent, 0)}`;
}

/**
 * Price at a favourable (TP) or adverse (SL) move of `percent` from entry,
 * mirrored for shorts.
 */
export function priceAtMove(
  entryPrice: number,
  percent: number,
  side: PositionSide,
  kind: ProtectionKind
): number {
  const up = (side === "LONG") === (kind === "takeProfit");
  return entryPrice * (up ? 1 + percent / 100 : 1 - percent / 100);
}

/** True when a stop loss at `price` sits at or past `liquidationPrice`. */
export function isPastLiquidation(
  price: number,
  liquidationPrice: number | null | undefined,
  side: PositionSide
): boolean {
  if (!liquidationPrice || !(liquidationPrice > 0)) return false;
  return side === "LONG" ? price <= liquidationPrice : price >= liquidationPrice;
}

export interface ProtectionPill {
  percent: number;
  /** Price rounded for the input. */
  value: string;
  /** "+10%" / "−5%" as a favourable/adverse move. */
  label: string;
}

/** Quick-pick pills; stop loss pills past liquidation are left out. */
export function protectionPills(args: {
  kind: ProtectionKind;
  entryPrice: number;
  entryPriceText?: string | number | null;
  side: PositionSide;
  liquidationPrice?: number | null;
}): ProtectionPill[] {
  const { kind, entryPrice, side, liquidationPrice } = args;
  if (!(entryPrice > 0)) return [];
  const percents = kind === "takeProfit" ? TAKE_PROFIT_PILLS : STOP_LOSS_PILLS;
  return percents
    .map((percent) => {
      const price = priceAtMove(entryPrice, percent, side, kind);
      return { percent, price };
    })
    .filter(
      ({ price }) =>
        price > 0 &&
        !(kind === "stopLoss" && isPastLiquidation(price, liquidationPrice, side))
    )
    .map(({ percent, price }) => ({
      percent,
      value: roundProtectionPrice(price, args.entryPriceText ?? entryPrice),
      label: `${kind === "takeProfit" ? "+" : "−"}${percent}%`,
    }));
}

/**
 * Starting price for a new TP/SL: TP +10% / SL −5% from entry (mirrored for
 * shorts). A stop loss that would sit past liquidation is moved halfway
 * between entry and liquidation. If price has already run past that level
 * (wrong side of mark), the same move is taken from the mark instead.
 */
export function defaultProtectionPrice(args: {
  kind: ProtectionKind;
  entryPrice: number;
  entryPriceText?: string | number | null;
  markPrice?: number | null;
  side: PositionSide;
  liquidationPrice?: number | null;
}): string {
  const { kind, entryPrice, markPrice, side, liquidationPrice } = args;
  const percent =
    kind === "takeProfit" ? DEFAULT_TAKE_PROFIT_PERCENT : DEFAULT_STOP_LOSS_PERCENT;
  if (!(entryPrice > 0)) return "";
  let price = priceAtMove(entryPrice, percent, side, kind);
  const mark = markPrice && markPrice > 0 ? markPrice : null;
  if (mark && wrongSideOfMark(kind, price, mark, side)) {
    price = priceAtMove(mark, percent, side, kind);
  }
  if (kind === "stopLoss" && isPastLiquidation(price, liquidationPrice, side)) {
    const reference = mark ?? entryPrice;
    price = (reference + (liquidationPrice as number)) / 2;
  }
  return roundProtectionPrice(price, args.entryPriceText ?? entryPrice);
}

export function wrongSideOfMark(
  kind: ProtectionKind,
  price: number,
  markPrice: number,
  side: PositionSide
): boolean {
  const above = price > markPrice;
  const below = price < markPrice;
  if (kind === "takeProfit") return side === "LONG" ? !above : !below;
  return side === "LONG" ? !below : !above;
}

/** "Starts at +40%, closes after a 30% drop" (a rise for shorts). */
export function trailingSummary(
  activationPercent: number | null | undefined,
  distancePercent: number | null | undefined,
  side: PositionSide
): string | null {
  const parts: string[] = [];
  if (activationPercent != null && activationPercent > 0) {
    parts.push(`Starts at +${trimNumber(activationPercent, 2)}%`);
  }
  if (distancePercent != null && distancePercent > 0) {
    const verb = side === "LONG" ? "drop" : "rise";
    parts.push(
      `${parts.length ? "closes" : "Closes"} after a ${trimNumber(distancePercent, 2)}% ${verb}`
    );
  }
  return parts.length ? parts.join(", ") : null;
}

/** "Binance" from "binance", "binance-demo", "gateio" → "Gate". */
export function exchangeDisplayName(
  exchangeName?: string | null,
  exchangeCode?: string | null
): string {
  if (exchangeName && exchangeName.trim()) return exchangeName.trim();
  const code = (exchangeCode ?? "").replace(/-demo$/, "").toLowerCase();
  const names: Record<string, string> = {
    binance: "Binance",
    bybit: "Bybit",
    gateio: "Gate",
    kucoin: "KuCoin",
    aster: "Aster",
    hyperliquid: "Hyperliquid",
  };
  return names[code] ?? "the exchange";
}

/** Positions whose TP/SL can be read and changed on the exchange. */
export function canManageProtection(position: {
  positionId?: string | null;
  existsOnExchange?: boolean | null;
}): boolean {
  const id = position.positionId ?? "";
  return Boolean(id) && !id.startsWith("db#") && position.existsOnExchange !== false;
}

/**
 * Where the trailing stop loss starts and, once it has started, the price it
 * would close at now. The best price is the highest of the peak the server
 * tracked (pump-detector), the candles since the open and the mark price
 * (lowest for a short), so the line moves with the chart.
 */
export function trailingChartLevels(args: {
  entryPrice: number;
  positionType: PositionSide;
  activationPercent?: number | null;
  distancePercent?: number | null;
  peakPrice?: number | null;
  markPrice?: number | null;
  candles?: { startTime: number; highPrice: string; lowPrice: string }[];
  openedAt?: string | null;
}): { startPrice: number | null; stopPrice: number | null } {
  const { entryPrice, positionType } = args;
  if (!(entryPrice > 0)) return { startPrice: null, stopPrice: null };
  // Same defaults and rule as common/src/utils/trailing-take-profit.ts
  // (kept import-free for Jest).
  const activation =
    args.activationPercent != null && args.activationPercent >= 0
      ? args.activationPercent
      : 40;
  const distance =
    args.distancePercent != null && args.distancePercent > 0
      ? args.distancePercent
      : 30;
  const long = positionType === "LONG";
  const openedMs = args.openedAt ? new Date(args.openedAt).getTime() : NaN;
  const prices: number[] = [];
  if (args.peakPrice && args.peakPrice > 0) prices.push(args.peakPrice);
  if (args.markPrice && args.markPrice > 0) prices.push(args.markPrice);
  for (const candle of args.candles ?? []) {
    if (Number.isFinite(openedMs) && candle.startTime < openedMs) continue;
    const price = Number(long ? candle.highPrice : candle.lowPrice);
    if (price > 0) prices.push(price);
  }
  const best = prices.length
    ? long
      ? Math.max(...prices)
      : Math.min(...prices)
    : entryPrice;
  const favourableMove = long
    ? (best / entryPrice - 1) * 100
    : (1 - best / entryPrice) * 100;
  if (favourableMove + 1e-9 >= activation) {
    const stopPrice = long
      ? best * (1 - distance / 100)
      : best * (1 + distance / 100);
    return { startPrice: null, stopPrice };
  }
  const startPrice = long
    ? entryPrice * (1 + activation / 100)
    : entryPrice * (1 - activation / 100);
  return { startPrice, stopPrice: null };
}
